import { type ChangeEventHandler, useCallback, useState } from 'react'

import { useDropzone } from 'react-dropzone'
import {
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'

import {
  useCandidate,
  useCreateCandidate,
  useUploadCandidateCV,
} from '@/react-query/candidate'
import {
  useInterview,
  useUpdateInterview,
} from '@/react-query/interview/interview'
import { useIsATSConnected, useIsViewOnlyMember } from '@/react-query/team'
import { PlusIcon } from '@/views/assets/PlusIcon'
import FileUploader from '@/views/components/FileUploader'
import { EditCVModal } from '@/views/screens/Interview/components/CandidateCard/CreateCandidate/EditCVModal'
import { InputATSCandidate } from '@/views/screens/Interview/components/CandidateCard/CreateCandidate/InputATSCandidate'
import { InputLinkedinCandidate } from '@/views/screens/Interview/components/CandidateCard/CreateCandidate/InputLinkedinCandidate'

/**
 * The empty candidate card. Uploading a file creates the candidate first; the
 * card then switches to EditCandidateWithAttachments, which shows the upload
 * in progress (the upload outlives this component).
 */
export const CreateCandidateWithAttachments = () => {
  const [isEnsuringCandidate, setIsEnsuringCandidate] = useState(false)

  const isViewOnlyUser = useIsViewOnlyMember()
  const isATSConnected = useIsATSConnected()
  const toast = useToast()

  const createCandidate = useCreateCandidate()
  const candidate = useCandidate()
  const uploadCandidateCV = useUploadCandidateCV()
  const updateInterview = useUpdateInterview()
  const { data: interview } = useInterview()
  const interviewType = interview?.type

  const { isOpen, onClose, onOpen } = useDisclosure()

  const handleFilesUpload = useCallback(
    async (files: File[]) => {
      if (!files.length) {
        return
      }

      let createdCandidateId: string | undefined
      try {
        let candidateId = candidate.data?.id
        if (!candidateId) {
          setIsEnsuringCandidate(true)
          try {
            const created = await createCandidate.mutateAsync({
              candidate: {},
            })
            createdCandidateId = created?.id
            candidateId = created?.id
          } finally {
            setIsEnsuringCandidate(false)
          }
        }
        if (!candidateId) {
          return
        }

        await uploadCandidateCV.mutateAsync({ candidateId, files })
      } catch (error) {
        console.error(error)
        // Do not leave behind the empty candidate created for this upload.
        if (createdCandidateId) {
          updateInterview
            .mutateAsync({ candidateId: null })
            .catch((unlinkError) => console.error(unlinkError))
        }
        toast({
          title: 'Could not upload the file',
          description: 'Please try again.',
          status: 'error',
          isClosable: true,
        })
      }
    },
    [
      candidate.data?.id,
      createCandidate,
      uploadCandidateCV,
      updateInterview,
      toast,
    ],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      void handleFilesUpload(acceptedFiles)
    },
    [handleFilesUpload],
  )

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'application/msword': ['.doc'],
    },
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  if (isViewOnlyUser) {
    return null
  }

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { files } = event.target
    if (!files || !files.length) {
      return
    }

    void handleFilesUpload(Array.from(files))
    event.target.value = ''
  }

  return (
    <Box flex={1} flexDirection={'column'} {...getRootProps()}>
      <InputLinkedinCandidate />
      {isATSConnected && (
        <Flex alignItems="center" mt={5}>
          <Text display={'inline'} color={'blackAlpha.700'}>
            or{' '}
          </Text>
          <Box ml={3} flex={1}>
            <InputATSCandidate />
          </Box>
        </Flex>
      )}
      {interviewType === 'NORMAL' && (
        <Flex alignItems="center" mt={5}>
          <Text display={'inline'} color={'blackAlpha.700'}>
            or{' '}
          </Text>
          <FileUploader
            name="candidateFiles"
            id="candidateFiles"
            acceptedFileTypes=".pdf,.doc,.docx"
            onFileChange={handleFileChange}
            isLoading={isEnsuringCandidate}
            ml={3}
            inputProps={{
              ...getInputProps(),
              multiple: true,
            }}
            inputRef={inputRef}
          >
            Upload files...
          </FileUploader>
        </Flex>
      )}
      <Flex alignItems="center" mt={5}>
        <Text display={'inline'} color={'blackAlpha.700'}>
          or{' '}
        </Text>
        <Button
          variant={'brand-solid-white'}
          ml={3}
          onClick={(event) => {
            event.preventDefault()
            onOpen()
          }}
        >
          {!candidate?.data?.id && (
            <>
              <PlusIcon stroke={'black'} mr={2} />{' '}
            </>
          )}
          {candidate?.data?.id ? 'Edit...' : 'Add manually'}
        </Button>
      </Flex>
      <EditCVModal isOpen={isOpen} onClose={onClose} />
      {isDragActive && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={100}
          display="flex"
          justifyContent="center"
          alignItems="center"
          rounded={'2.5rem'}
          bg={'#F5F5F5'}
          border={'2px dashed #C4C4C4'}
        >
          <Text color={'blackAlpha.700'} fontWeight={'bold'} fontSize={'xl'}>
            Drop candidate files here
          </Text>
        </Box>
      )}
    </Box>
  )
}
